export const COOKIE_CONSENT_COOKIE_NAME = 'cookie_consent';
export const COOKIE_CONSENT_DURATION_MS = 365 * 24 * 60 * 60 * 1000;
export const COOKIE_CONSENT_DURATION_SECONDS = COOKIE_CONSENT_DURATION_MS / 1000;
export const COOKIE_CONSENT_CHANGE_EVENT_NAME = 'chargewipe-cookie-consent-change';
export const COOKIE_CONSENT_REQUEST_EVENT_NAME = 'chargewipe-cookie-consent-request';

export type CookieConsentChoice = 'necessary' | 'selection' | 'all' | 'rejected';
export type CookieConsentCategory = 'necessary' | 'preferences' | 'statistics' | 'marketing';
export type CookieConsentCategories = Record<CookieConsentCategory, boolean>;

export interface CookieConsentRecord {
  version: 1;
  choice: CookieConsentChoice;
  categories: CookieConsentCategories;
  createdAt: number;
  expiresAt: number;
}

const sharedCookieDomains = [
  { domain: '.bestfulfill.top', hostname: 'bestfulfill.top' },
  { domain: '.chargewipe.com', hostname: 'chargewipe.com' },
];

const categoryDefaults: CookieConsentCategories = {
  necessary: false,
  preferences: false,
  statistics: false,
  marketing: false,
};

export function createCookieConsentRecord(
  choice: CookieConsentChoice,
  createdAt = Date.now(),
  categories: Partial<CookieConsentCategories> = {},
): CookieConsentRecord {
  const necessaryRequiredByChoice = choice === 'necessary' || choice === 'all';
  const normalizedCategories = {
    ...categoryDefaults,
    ...categories,
    necessary: necessaryRequiredByChoice || categories.necessary === true,
  };

  return {
    version: 1,
    choice,
    categories: normalizedCategories,
    createdAt,
    expiresAt: createdAt + COOKIE_CONSENT_DURATION_MS,
  };
}

export function shouldShowCookieConsent(record: CookieConsentRecord | null, now = Date.now()) {
  return !record || typeof record.expiresAt !== 'number' || record.expiresAt <= now;
}

export function getCookieValue(cookieSource: string, name = COOKIE_CONSENT_COOKIE_NAME) {
  if (!cookieSource) {
    return null;
  }

  const encodedName = encodeURIComponent(name);
  const cookie = cookieSource.split('; ').find((item) => item.startsWith(`${encodedName}=`));

  try {
    return cookie ? decodeURIComponent(cookie.split('=').slice(1).join('=')) : null;
  } catch {
    return null;
  }
}

export function readCookieConsent(cookieSource: string, now = Date.now()) {
  const value = getCookieValue(cookieSource);
  if (!value) {
    return null;
  }

  try {
    const record = JSON.parse(value) as CookieConsentRecord;
    if (shouldShowCookieConsent(record, now)) {
      return null;
    }

    return record;
  } catch {
    return null;
  }
}

export function hasNecessaryCookieConsent(cookieSource: string, now = Date.now()) {
  const record = readCookieConsent(cookieSource, now);

  return record?.categories?.necessary === true;
}

export function getCookieDomainAttribute(hostname: string) {
  const normalizedHostname = hostname.toLowerCase();
  const matchedDomain = sharedCookieDomains.find(
    (item) =>
      normalizedHostname === item.hostname || normalizedHostname.endsWith(`.${item.hostname}`),
  );

  return matchedDomain ? `; Domain=${matchedDomain.domain}` : '';
}

export function getSecureCookieAttribute(protocol: string) {
  return protocol === 'https:' ? '; Secure' : '';
}

export function createCookieConsentCookie(
  record: CookieConsentRecord,
  locationLike: Pick<Location, 'hostname' | 'protocol'>,
  name = COOKIE_CONSENT_COOKIE_NAME,
) {
  const encodedName = encodeURIComponent(name);
  const encodedValue = encodeURIComponent(JSON.stringify(record));
  const domainAttribute = getCookieDomainAttribute(locationLike.hostname);
  const secureAttribute = getSecureCookieAttribute(locationLike.protocol);

  return `${encodedName}=${encodedValue}; Path=/; Max-Age=${COOKIE_CONSENT_DURATION_SECONDS}; SameSite=Lax${domainAttribute}${secureAttribute}`;
}

export function createCookieConsentRemovalCookie(name = COOKIE_CONSENT_COOKIE_NAME) {
  return `${encodeURIComponent(name)}=; Path=/; Max-Age=0; SameSite=Lax`;
}

export function writeCookieConsent(
  documentLike: Pick<Document, 'cookie'> | undefined,
  record: CookieConsentRecord,
  locationLike: Pick<Location, 'hostname' | 'protocol'> | undefined,
) {
  if (!documentLike || !locationLike) {
    return;
  }

  try {
    documentLike.cookie = createCookieConsentRemovalCookie();
    documentLike.cookie = createCookieConsentCookie(record, locationLike);
  } catch {
    // Keep the UI responsive if a browser policy blocks cookie writes.
  }
}

export function requestCookieConsent(reason = 'action') {
  if (typeof window === 'undefined') {
    return;
  }

  window.dispatchEvent(
    new CustomEvent(COOKIE_CONSENT_REQUEST_EVENT_NAME, {
      detail: { reason },
    }),
  );
}

export function ensureNecessaryCookieConsent(reason = 'action') {
  if (typeof document === 'undefined') {
    return false;
  }

  if (hasNecessaryCookieConsent(document.cookie)) {
    return true;
  }

  requestCookieConsent(reason);
  return false;
}
